import React, { useEffect, useState } from "react";
import { Headphones, Save, RotateCcw } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import CustomDropdown from "./CustomDropdown";

// Live chat behaviour per AI agent (stored on the server, /api/live-chat/settings). Every value has a platform
// default; the card shows the current values and saves only what changed.

const NUMBER_FIELDS = [
  { key: "max_chats_per_agent", label: "Max chats per agent", unit: "chats", hint: "Open chats one agent handles at the same time. 0 = no limit." },
  { key: "wait_timeout_minutes", label: "Wait for an agent", unit: "min", hint: "If no agent accepts within this time, the chat goes back to the bot. 0 = wait." },
  { key: "auto_resolve_hours", label: "Auto-resolve idle chats", unit: "hours", hint: "Agent chats without any message for this long are closed. 0 = never." },
  { key: "away_after_minutes", label: "Mark agent Away after", unit: "min", hint: "No sign of the admin app for this long = Away (no new chats)." },
  { key: "conversation_expiry_minutes", label: "Conversation expires after", unit: "min", hint: "Idle conversations start fresh after this time. 0 = never." },
];

const TEXT_FIELDS = [
  { key: "no_agent_message", label: "Message when no agent is available", showFor: "bot_continues" },
  { key: "close_message", label: "Closing message when no agent is available", showFor: "message_and_close" },
  { key: "contact_request_message", label: "Ask for contact details" },
  { key: "resolve_message", label: "Message when an agent closes the chat" },
];

const NO_AGENT_OPTIONS = [
  { value: "bot_continues", label: "Bot continues the conversation" },
  { value: "message_and_close", label: "Show a fixed message and close" },
];

const REFRESH_OPTIONS = [
  { value: "keep", label: "Keep the conversation" },
  { value: "new", label: "Start a new conversation" },
];

const inputClass =
  "w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-zinc-900 text-[11px] font-medium focus:outline-none focus:border-zinc-300";
const dropdownClass =
  "w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-zinc-900 text-[11px] font-medium flex items-center justify-between";

const Toggle = ({ checked, onChange }) => (
  <label className="relative inline-flex items-center cursor-pointer shrink-0">
    <input type="checkbox" checked={!!checked} onChange={onChange} className="sr-only peer" />
    <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
  </label>
);

const Label = ({ children }) => (
  <label className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{children}</label>
);

const LiveChatSettings = () => {
  const { showToast } = useToast();
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [limits, setLimits] = useState({});
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/live-chat/settings", { skipToast: true })
      .then((res) => {
        if (!alive) return;
        setSaved(res.settings);
        setForm(res.settings);
        setLimits(res.limits || {});
        setDefaults(res.defaults || {});
      })
      .catch(() => {
        // agents without admin rights cannot see or change these settings: hide the card
        if (alive) setForbidden(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (forbidden || !form) return null;

  const changed = Object.keys(form).filter((k) => JSON.stringify(form[k]) !== JSON.stringify(saved[k]));
  const set = (key, value) => setForm({ ...form, [key]: value });

  const save = async () => {
    const body = {};
    for (const key of changed) {
      const limit = limits[key];
      if (limit) {
        const n = Number(form[key]);
        if (!Number.isInteger(n) || n < limit.min || n > limit.max) {
          showToast(`${key.replace(/_/g, " ")}: enter a whole number between ${limit.min} and ${limit.max}`, "error");
          return;
        }
        body[key] = n;
      } else {
        body[key] = typeof form[key] === "string" ? form[key].trim() : form[key];
      }
    }
    setSaving(true);
    try {
      const res = await api.put("/live-chat/settings", body);
      setSaved(res.settings);
      setForm(res.settings);
      showToast("Live chat settings saved", "success");
    } catch (err) {
      // the API client already shows the server's message
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs overflow-hidden w-full transition-all">
      <div className="bg-zinc-50/50 p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-zinc-900 text-white flex items-center justify-center shadow-xs">
            <Headphones size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-zinc-900">Live Chat</h3>
            <p className="text-xs text-zinc-400">How chats are handed to your agents and when they end</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {changed.length > 0 && (
            <button
              type="button"
              onClick={() => setForm(saved)}
              className="px-3 py-2 rounded-xl text-[11px] font-semibold text-zinc-500 hover:bg-zinc-100 flex items-center gap-1.5"
            >
              <RotateCcw size={13} /> Undo
            </button>
          )}
          <button
            type="button"
            disabled={!changed.length || saving}
            onClick={save}
            className="px-4 py-2 rounded-xl text-[11px] font-semibold bg-zinc-900 text-white disabled:opacity-40 flex items-center gap-1.5"
          >
            <Save size={13} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="p-6 sm:p-7 space-y-6">
        {/* Routing */}
        <div className="flex items-center justify-between p-4 bg-zinc-50/50 rounded-xl border border-zinc-100">
          <div className="space-y-0.5 pr-4">
            <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Online agents only</h4>
            <p className="text-[10px] text-zinc-400 font-medium">
              New chats go only to agents set to Online with the admin app open.
            </p>
          </div>
          <Toggle checked={form.only_online_agents} onChange={() => set("only_online_agents", !form.only_online_agents)} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {NUMBER_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label>{f.label}</Label>
              <div className="relative">
                <input
                  type="number"
                  min={limits[f.key]?.min}
                  max={limits[f.key]?.max}
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                  className={`${inputClass} pr-14`}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400 font-semibold">{f.unit}</span>
              </div>
              <p className="text-[10px] text-zinc-400">
                {f.hint} Default {defaults[f.key]}.
              </p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>When no agent is available</Label>
            <CustomDropdown
              options={NO_AGENT_OPTIONS}
              value={form.no_agent_action}
              onChange={(val) => set("no_agent_action", val)}
              buttonClassName={dropdownClass}
            />
          </div>
          <div className="space-y-1.5">
            <Label>When the visitor reloads the page</Label>
            <CustomDropdown
              options={REFRESH_OPTIONS}
              value={form.conversation_on_refresh}
              onChange={(val) => set("conversation_on_refresh", val)}
              buttonClassName={dropdownClass}
            />
            {form.conversation_on_refresh === "new" && (
              <p className="text-[10px] text-amber-600">
                Every page load (also moving to another page of the site) starts a new conversation and ends an open agent chat.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-zinc-50/50 rounded-xl border border-zinc-100">
            <div className="space-y-0.5 pr-4">
              <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Ask for contact details</h4>
              <p className="text-[10px] text-zinc-400 font-medium">Only when the visitor has not given an email or phone yet.</p>
            </div>
            <Toggle checked={form.ask_contact_if_missing} onChange={() => set("ask_contact_if_missing", !form.ask_contact_if_missing)} />
          </div>
          <div className="flex items-center justify-between p-4 bg-zinc-50/50 rounded-xl border border-zinc-100">
            <div className="space-y-0.5 pr-4">
              <h4 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">"End chat" button</h4>
              <p className="text-[10px] text-zinc-400 font-medium">Visitors can close the conversation (with confirmation).</p>
            </div>
            <Toggle checked={form.end_chat_enabled} onChange={() => set("end_chat_enabled", !form.end_chat_enabled)} />
          </div>
        </div>

        <div className="space-y-4">
          {TEXT_FIELDS.filter((f) => !f.showFor || f.showFor === form.no_agent_action)
            .filter((f) => f.key !== "contact_request_message" || form.ask_contact_if_missing)
            .map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label>{f.label}</Label>
                <textarea
                  rows={2}
                  maxLength={500}
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  className={`${inputClass} resize-none`}
                />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default LiveChatSettings;
